import React, { useCallback, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { Box, Button } from '@mui/material';
import { useIntl } from 'react-intl';
import { useProjectId } from 'store/project';
import { useLocation } from 'react-router-dom';

import { formatTranslation } from 'intl';
import { CommonTranslationKeys } from 'intl/keys';
import { useAppNavigation } from 'contexts/navigation';
import { signalNavigationOutsideDocumentation } from 'store/documentation/hooks/useDocumentationUrlNavigation';

import { HashMap, LabelledEntity } from 'shared/types/commonView';
import { LevelOnView } from 'shared/domain/level/types/view';
import { Issue } from 'setup/types/core';
import { AfterDoneResult } from 'views/issue/wizard/afterDoneResult';
import { CreateIssueDialog } from 'views/issue/wizard/createIssueDialog';

import { floatingButtonContainerSx, floatingButtonSx } from './styles';

interface InitialValues {
  site: LabelledEntity;
  level?: LabelledEntity;
}

interface FloatingCreateIssueButtonProps {
  availableSites: LabelledEntity[];
  availableLevelsPerSite?: HashMap<LevelOnView[]>;
  initialValues?: InitialValues;
  loadingLevels: boolean;
}

export const FloatingCreateIssueButton: React.FC<FloatingCreateIssueButtonProps> = ({
  availableSites,
  availableLevelsPerSite,
  initialValues,
  loadingLevels,
}) => {
  const intl = useIntl();
  const location = useLocation();
  const projectId = useProjectId();
  const { goToIssueDetails } = useAppNavigation();

  const [dialogOpen, setDialogOpen] = useState(false);

  const label = formatTranslation(
    intl,
    CommonTranslationKeys.toolbarTitleCreateIssue
  );

  const handleOpen = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleAfterDone = useCallback(
    (issue: Issue | undefined, result: AfterDoneResult) => {
      if (result === AfterDoneResult.saved && issue && projectId) {
        const returnUrl = `${location.pathname}${location.search}`;
        signalNavigationOutsideDocumentation();
        goToIssueDetails(projectId, issue._id, {
          state: { issue, returnUrl },
        });
        setDialogOpen(false);
        return;
      }

      handleClose();
    },
    [
      projectId,
      location.pathname,
      location.search,
      goToIssueDetails,
      handleClose,
    ]
  );

  // Don't show button if no available sites
  // (availableSites is empty when file has no documentationId)
  if (!availableSites.length) {
    return null;
  }

  const disabled = loadingLevels;

  return (
    <>
      <Box sx={floatingButtonContainerSx}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          disabled={disabled}
          onClick={handleOpen}
          sx={floatingButtonSx}
        >
          {label}
        </Button>
      </Box>

      <CreateIssueDialog
        open={dialogOpen}
        afterDone={handleAfterDone}
        initialValues={initialValues}
        availableSites={availableSites}
        availableLevelsPerSite={availableLevelsPerSite}
      />
    </>
  );
};
